'use strict';

// external imports
import {Request} from 'express';

// shared imports
import Joi from '~/shared/src/utilities/joiExtended';
import StackError from '~/shared/src/base/StackError';

// backend imports
//////////////////////////
////////
//////////
////////////
////////
////////////////////////////////////////
//////////

export const schemaSchema = Joi.string()
                               .valid( 'CFG', 'DATA' )
                               .default( 'DATA' )
                               .description( 'The database schema to load data and meta data from.' );
export const statusSchema = Joi.string()
                               .default( 'active' )
                               .when( 'schema', {
                                   is       : 'CFG',
                                   then     : Joi.valid( 'all', 'all-non-history', 'active', 'history', 'deleted' ),
                                   otherwise: Joi.valid( 'all', 'all-non-history', 'active', 'planned', 'template', 'history', 'deleted', 'active-planned', 'active-planned-template', 'imported' )
                               } )
                               .description( 'The status of data to select.' );
export const timestampSchema = Joi.string()
                                  .default( 'now' )
                                  .description( 'The timestamp to select data at.' );
export const timezoneSchema = Joi.string()
                                 .timezone()
                                 .default( 'Europe/Berlin' )
                                 .description( 'The timezone to select data in.' );
export const requestBaseParametersSchema = Joi.object( {
                                                  schema     : schemaSchema,
                                                  status     : statusSchema,
                                                  timestamp  : timestampSchema,
                                                  timezone   : timezoneSchema,
                                                  webSocketId: Joi.string().allow( '' )
                                              } )
                                              .description( 'The full request base parameters schema.' );

export type BaseRequestParameters = {
    schema? : Schema,
    status? : Status,
    timestamp? : string;
    timezone? : string;
    webSocketId? : string;
}


class BaseRequest {
    #_schema : Schema;
    #_status : Status;
    #_timezone : string;
    #_timestamp : string;
    #_webSocketId : string;

    constructor( request : Request ) {
        const parameters = {
            schema     : request?.body?.schema ?? request?.user?.application?.schema,
            status     : request?.body?.status,
            timestamp  : request?.body?.timestamp,
            timezone   : request?.body?.timezone,
            webSocketId: request?.body?.webSocketId
        };

        const validationResult = requestBaseParametersSchema.validate( parameters, { abortEarly: false } );

        if ( validationResult.error ) {
            const errorObj = new StackError( 'Initializing base request attributes object raises validation error(s): ' + validationResult.error.message );
            errorObj.add( validationResult.error.details.map( errorDetail => errorDetail.message ) );

            // the request carries an unusable schema/status/timezone - that is on
            // the caller, not on us
            errorObj.httpCode = 400;

            throw ( errorObj );
        }

        if ( validationResult.warning ) {
            warn( validationResult.warning );
        }

        const initializedParameters = validationResult.value as BaseRequestParameters;

        this.#_schema = initializedParameters.schema!;
        this.#_status = initializedParameters.status!;
        this.#_timezone = initializedParameters.timezone!;
        this.#_timestamp = initializedParameters.timestamp!;
        this.#_webSocketId = initializedParameters.webSocketId!;

        debug( 'base request parameters object set' );
        debug( `   schema     : ${this.#_schema}` );
        debug( `   status     : ${this.#_status}` );
        debug( `   timezone   : ${this.#_timezone}` );
        debug( `   timestamp  : ${this.#_timestamp}` );
        debug( `   webSocketId: ${this.#_webSocketId}` );
        debug( '-'.repeat( global.logSeparatorWidth ) );
    }

    get schema() {
        return this.#_schema;
    }

    get status() {
        return this.#_status;
    }

    get timezone() {
        return this.#_timezone;
    }

    get timestamp() {
        return this.#_timestamp;
    }

    get webSocketId() {
        return this.#_webSocketId;
    }
}

export default BaseRequest;
